/**
 * Route Utilities
 *
 * Utilities for extracting route information like titles from route configuration.
 *
 * Note: Detail pages should use useTabTitle hooks to set their own titles
 * dynamically after loading data. This file only provides fallback titles
 * for list/menu pages.
 */

// Static route titles for list/menu pages only
// Detail pages should use useTabTitle() to set their own titles
const ROUTE_TITLES: Record<string, string> = {
  '/': 'Dashboard', // Root redirects to /dashboard
  '/dashboard': 'Dashboard',
  '/pending-instructions': 'Pending Instructions',
  '/pending-instructions/payments': 'Payments',
  '/pending-instructions/instructions': 'Instructions',
  '/pending-instructions/cra-form': 'CRA Form',
  '/pending-instructions/digital-deposits': 'Digital Deposits',
  '/users': 'Users & Accounts',
  '/solution-application': 'Solution Application',
  '/solution-applications': 'Solution Applications',
  '/client-account': 'Client Accounts',
  '/client-account/wallet-accounts': 'Wallet Accounts',
  '/client-account/client-deposit-accounts': 'Client Deposit Bank Accounts',
  '/client-account/od-account': 'OD Account',
  '/client-account/bank-payout-whitelist': 'Bank Payout Whitelist',
  '/client-documents': 'Client Documents',
  '/document-templates': 'Document Templates',
  '/name-check': 'Name Check',
  '/name-check/settings': 'WorldCheck Settings',
  '/instructions': 'Instructions',
  '/instructions/all': 'All Instructions',
  '/instructions/scheduled': 'Scheduled Instructions',
  '/instructions/general-instruction': 'General Instructions',
  '/instructions/fiat-withdraws': 'Fiat Withdraw',
  '/instructions/fiat-deposits': 'Fiat Deposits',
  '/instructions/cash-in-out': 'Cash In/Out',
  '/instructions/digital-withdraws': 'Digital Withdraw',
  '/instructions/digital-deposits': 'Digital Deposits',
  '/instructions/transfers': 'Transfers',
  '/instructions/bill-payments': 'Bill Payments',
  '/instructions/legacy-bill-payments': 'Bill Payments (Legacy)',
  '/instructions/exchanges': 'Exchanges',
  '/instructions/remittances': 'Remittances',
  '/instructions/unionpay-remittance': 'UnionPay Remittance',
  '/instructions/alipay-remittance': 'Alipay Remittance',
  '/scheduled-instructions': 'Scheduled Instructions',
  '/digital-custody': 'Digital Custody',
  '/digital-custody/deposits': 'Digital Deposits',
  '/digital-custody/deposits/pending': 'Pending Deposits',
  '/digital-custody/withdraws': 'Digital Withdraws',
  '/digital-custody/address-screening': 'Address Screening',
  '/digital-custody/screening-history': 'Screening History',
  '/digital-custody/batch-rescreen': 'Batch Re-screening',
  '/digital-custody/relationship-graph': 'Relationship Analysis',
  '/kyt-screening': 'KYT Screening',
  '/compliance/cases': 'Cases',
  '/compliance/cases/rules': 'Case Rules',
  '/compliance/kyt-screening': 'KYT Screening',
  '/invoices': 'Invoices',
  '/operators': 'Operators',
  '/operational-accounts': 'Operational Accounts',
  '/worldpay': 'WorldPay',
  '/card-fraud-system': 'Card Fraud System',
  '/cms': 'Card & Credit',
  '/cms/cards': 'Cards',
  '/cms/card-kyc': 'Issuer Card KYC',
  '/cms/issuer-card-kyc': 'Issuer Card KYC',
  '/cms/card-kyc-status': 'Card KYC',
  '/cms/card-factory': 'Cards Factory',
  '/cms/card-account': 'Card Account',
  '/cms/card-accounts': 'Card Accounts',
  '/cms/card-cupd-balances': 'Card CUPD Balances',
  '/cms/card-balances': 'Card Balances',
  '/cms/card-repayment': 'Card Repayment',
  '/cms/card-application': 'Card Application',
  '/cms/card-renewal': 'Card Renewal',
  '/cms/card-pending-embossed': 'Card Pending Embossed',
  '/cms/asset-value-config': 'Global LTV Config',
  '/cms/card-products-v2': 'Card Products V2',
  '/cms/transactions': 'Transactions',
  '/cms/card-disputes': 'Card Disputes',
  '/cms/force-posting': 'Card Force Posting',
  '/cms/card-account-interest': 'Card Account Interest',
  '/cms/card-name-check': 'Card Name Check',
  '/cms/notification-log': 'Card Code Delivery',
  '/cms/card-oob-delivery': 'OOB Verification History',
  '/securities': 'Securities',
  '/securities/brokers': 'Brokers',
  '/securities/accounts': 'Accounts',
  '/earn': 'Earn',
  '/earn/offers': 'Offers',
  '/earn/subscriptions': 'Subscriptions',
  '/earn/positions': 'Positions',
  '/alternatives': 'Alternatives',
  '/insurance': 'Insurance',
  '/nfts': 'NFT',
  '/reporting': 'Reporting',
  '/reporting/standard': 'Standard Reports',
  '/reporting/accounting': 'Accounting Reports',
  '/reporting/journal': 'Journal Reports',
  '/reporting/history': 'Report History',
  '/reporting/sensitive-card': 'Sensitive Card Report',
  '/reporting/configurations': 'Report Configurations',
  '/accounting': 'Accounting',
  '/accounting/world-pay': 'World Pay',
  '/accounting/journal-entries': 'Journal Entries',
  '/accounting/book-entries': 'Book Entries',
  '/accounting/ledger-account': 'Ledger Account',
  '/accounting/balance-sheet': 'Balance Sheet',
  '/accounting/bank-available-balance': 'Bank Available Balance',
  '/accounting/tags': 'Accounting Tag Management',
  '/accounting/commission-details': 'Commission Detail',
  '/accounting/sales-revenue-summary': 'Sales Revenue Summary',
  '/accounting/sales-revenue-statistics': 'Sales Team Revenue Statistics',
  '/accounting/revenue-breakdown': 'Revenue Breakdown',
  '/accounting/operational-accounts': 'Operational Accounts',
  '/accounting/utgl-pnl': 'UTGL PNL',
  '/cost-and-fees': 'Fees & Commission',
  '/cost-and-fees/cost-management': 'Cost Management',
  '/cost-and-fees/fee-markup-management': 'Markup Management',
  '/cost-and-fees/fee-schedule': 'Fee Schedule',
  '/cost-and-fees/fee-schedule-v2': 'New Fee Schedule',
  '/cost-and-fees/users-fee-schedule': 'Users Fee Schedule',
  '/cost-and-fees/cobrands-fee-schedule': 'Cobrands Fee Schedule',
  '/cost-and-fees/commission-management': 'Commission Management',
  '/cost-and-fees/fee-setting-request': 'Fee Setting Request',
  '/cost-and-fees/agent-onboarding': 'Agent Onboarding Settings',
  '/transaction-monitoring': 'Transaction Monitoring',
  '/transaction-monitoring/kyt-screening': 'KYT Screening',
  '/transaction-monitoring/fraud-transactions': 'Fraud Transactions',
  '/transaction-monitoring/card-fraud-rules-configuration': 'Card Rules Configuration',
  '/rate-source': 'Rate Source',
  '/rate-source/configuration': 'Configuration',
  '/rate-source/rate-reference': 'Rate Reference',
  '/rate-source/rate-board': 'Rateboard',
  '/precious-metal': 'Precious Metal',
  '/precious-metal/products': 'Products',
  '/precious-metal/instructions': 'Instructions',
  '/precious-metal/positions': 'Positions',
  '/fund': 'Fund',
  '/fund/positions': 'Positions',
  '/fund/instructions': 'Instructions',
  '/fund/ifast-accounts': 'iFast Account List',
  '/purchase-order': 'Purchase Order',
  '/tag-management': 'Tag Management',
  '/invitation-code': 'Invitation Code',
  '/relationship-managers': 'Relationship Managers',
  '/user-groups': 'User Groups',
  '/support-center': 'Support Center',
  '/support-center/all-requests': 'All Requests',
  '/support-center/my-requests': 'My Requests',
  '/support-center/new': 'Support Center',
  '/support-center/report-issue': 'Report Issue',
  '/support-center/feedback': 'Give Feedback',
  '/support-center/feature-request': 'Feature Request',
  '/support-center/client-issue': 'Client Issue',
  '/support-center/category-admin': 'Category Management',
  '/support-center/tag-admin': 'Ticket Tags',
  '/support-center/category-analytics': 'Category Analytics',
  '/workflows': 'Workflows',
  '/workflows/chat': 'AI Chat',
  '/workflows/rulesets': 'Rulesets',
  '/super': 'Super Powers',
  '/super-powers': 'Super Powers',
  '/super/operators': 'Operators',
  '/super/feature-flags': 'Feature Flags',
  '/developer-console': 'Developer Console',
  '/developer-console/digital-deposit-simulation': 'Digital Deposit Simulation',
  '/developer-console/feature-flag-output-overrides': 'Feature Flag Output Overrides',
  '/account/settings': 'Account Settings',
  '/cash-inventory': 'Cash Inventory',
  '/card-programs': 'Card Programs',
  '/client-consent': 'Consent Documents',
  '/client-consent/professional-investor': 'Professional Investor',
  '/whats-new': "What's New",
  '/manual': 'Operators Manual',
  '/app-disclosures': 'App disclosures',
  '/app-card-offers': 'Card Offers',
  '/app-card-offers/new': 'New App Card Offer',
  '/app-whats-new': "App What's New",
  '/accounting/unigroup-client-asset-balance': 'Client Asset Overview'
};

export type TabTitleSource = {
  title: string;
  path: string;
  history: Array<{ path: string }>;
  historyIndex: number;
};

function titleCaseFromKebab(segment: string): string {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Derive a title from URL path segments without consulting ROUTE_TITLES.
 * Used to detect stale auto-generated tab titles that should be normalized.
 */
export function getNaivePathTitle(pathname: string | undefined | null): string {
  if (typeof pathname !== 'string') {
    return 'Page';
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return 'Dashboard';
  }

  const lastSegment = segments[segments.length - 1];
  const isId = /^\d+$/.test(lastSegment) || /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(lastSegment);

  if (isId && segments.length >= 2) {
    return `${titleCaseFromKebab(segments[segments.length - 2])} Details`;
  }

  return titleCaseFromKebab(lastSegment);
}

/**
 * Replace auto-generated tab titles with canonical route titles from ROUTE_TITLES.
 */
export function normalizeTabTitle(storedTitle: string, pathname: string): string {
  const routeTitle = getRouteTitle(pathname);
  const naiveTitle = getNaivePathTitle(pathname);

  if (storedTitle === naiveTitle) {
    return routeTitle;
  }

  return storedTitle;
}

/**
 * Resolve the operator-facing title for a tab, applying route normalization.
 */
export function getTabDisplayTitle(tab: TabTitleSource): string {
  const currentPath = tab.history[tab.historyIndex]?.path || tab.path;
  return normalizeTabTitle(tab.title, currentPath);
}

/**
 * First path segment used to group overflow tabs (e.g. "accounting").
 */
export function getTabRouteGroupKey(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] || 'dashboard';
}

/**
 * Human-readable label for a tab route group key.
 */
export function getTabRouteGroupLabel(groupKey: string): string {
  return getRouteTitle(`/${groupKey}`);
}

/**
 * Get the title for a given route path.
 *
 * For list pages: Returns the static title from ROUTE_TITLES.
 * For detail pages: Returns a fallback title from the URL path.
 *   Detail pages should use useTabTitle() to set a proper title after loading data.
 */
export function getRouteTitle(pathname: string | undefined | null): string {
  // Defensive: handle non-string inputs gracefully
  if (typeof pathname !== 'string') {
    console.warn('[getRouteTitle] Expected string pathname, received:', typeof pathname, pathname);
    return 'Page';
  }

  // Check static routes first
  if (ROUTE_TITLES[pathname]) {
    return ROUTE_TITLES[pathname];
  }

  return getNaivePathTitle(pathname);
}

/**
 * Get the icon key for a given route path
 */
export function getRouteIcon(pathname: string): string | undefined {
  const iconMap: Record<string, string> = {
    '/dashboard': 'dashboard',
    '/users': 'team',
    '/pending-instructions': 'clock',
    '/instructions': 'fileText',
    '/client-documents': 'folder',
    '/document-templates': 'fileText',
    '/client-account': 'wallet',
    '/solution-application': 'solution',
    '/name-check': 'securityScan',
    '/cms': 'creditCard',
    '/earn': 'fund',
    '/reporting': 'barChart',
    '/accounting': 'accountBook',
    '/workflows': 'robot',
    '/super': 'setting',
    '/support-center': 'customerService',
    '/precious-metal': 'gold',
    '/digital': 'block',
    '/exchanges': 'swap',
    '/securities': 'fundView',
    '/insurance': 'insurance',
    '/alternatives': 'trophy'
  };

  for (const [path, icon] of Object.entries(iconMap)) {
    if (pathname.startsWith(path)) {
      return icon;
    }
  }

  return undefined;
}

/**
 * Check if a path is a detail page (ends with numeric ID or UUID)
 */
export function isDetailPage(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2) return false;
  const lastSegment = segments[segments.length - 1];
  // Match numeric IDs or UUIDs
  return /^\d+$/.test(lastSegment) || /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(lastSegment);
}

/**
 * Get the parent path for a detail page
 */
export function getParentPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length <= 1) return null;

  // Check if removing the last segment gives us a valid route
  const parentPath = '/' + segments.slice(0, -1).join('/');
  if (ROUTE_TITLES[parentPath]) {
    return parentPath;
  }

  // For paths like /users/123, parent is /users
  if (segments.length === 2 && /^\d+$/.test(segments[1])) {
    return '/' + segments[0];
  }

  return null;
}
